import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, MapPin, Phone, Syringe, Dna, Scale, Ruler, BookOpen, Play } from "lucide-react";
import { api } from "@/lib/api";

export default function PublicPassport() {
  const { dogId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [highlights, setHighlights] = useState(null);

  useEffect(() => {
    api.get(`/public/passport/${dogId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Passaporto non trovato"));
    // Diary highlights — only for authenticated users. Anonymous public
    // viewers get 401 and we silently hide the section.
    api.get(`/dogs/${dogId}/highlights`)
      .then((r) => setHighlights(r.data?.highlights || []))
      .catch(() => setHighlights([]));
  }, [dogId]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#FBF7F1] text-[#1A1A1A] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="font-display text-3xl">Passaporto non trovato</div>
          <div className="text-[#6B6B6B] mt-2">{err}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FBF7F1] text-[#1A1A1A] flex items-center justify-center">
        <div className="text-[#6B6B6B]">Caricamento...</div>
      </div>
    );
  }

  const d = data.dog;
  const em = data.emergency;

  return (
    <div className="min-h-screen bg-[#FBF7F1] text-[#1A1A1A] orio-grain">
      <header className="border-b border-[#F0EBE3] py-5 px-6 flex items-center justify-between max-w-5xl mx-auto">
        <a href="/" className="flex items-center gap-3">
          <div aria-hidden className="h-9 w-9 orio-logo-grad" />
          <span className="font-display text-xl">ORIO</span>
        </a>
        <div className="text-xs uppercase tracking-widest text-[#9A9A9A]">Passaporto digitale</div>
      </header>

      {em && (
        <div className="bg-[#FF3B30] text-[#1A1A1A] px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <AlertTriangle className="h-5 w-5" />
            <div>
              <div className="font-medium">Questo cane è attualmente segnalato come SMARRITO.</div>
              <div className="text-sm opacity-90">
                {em.last_seen_note} · Segnalato il {new Date(em.created_at).toLocaleString("it-IT")}
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="aspect-square rounded-3xl overflow-hidden border border-[#F0EBE3] bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] relative">
              {d.photo_url ? (
                <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center font-display text-8xl text-[#1A1A1A]/10">
                  {d.name?.[0]?.toUpperCase()}
                </div>
              )}
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-[#9A9A9A] mb-2">Cane</div>
            <h1 className="font-display text-5xl tracking-tight">{d.name}</h1>
            <div className="text-[#6B6B6B] mt-2">{d.breed || "Razza non specificata"}{d.sex ? ` · ${d.sex}` : ""}</div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {d.color && <Stat icon={Dna} label="Colore" value={d.color} />}
              {d.weight_kg && <Stat icon={Scale} label="Peso" value={`${d.weight_kg} kg`} />}
              {d.height_cm && <Stat icon={Ruler} label="Altezza" value={`${d.height_cm} cm`} />}
              {d.microchip && (
                <Stat
                  icon={Syringe}
                  label="Microchip"
                  value="🔒 Solo veterinari"
                />
              )}
            </div>

            {/* Privacy notice — reassures the owner that sensitive fields are private */}
            <div className="mt-3 text-[11px] text-[#9A9A9A] flex items-start gap-1.5">
              <span className="mt-0.5">🔒</span>
              <span>Il numero di microchip non è mai visibile pubblicamente. È accessibile solo a veterinari autorizzati e a te.</span>
            </div>

            {d.distinctive_traits && (
              <Section title="Tratti distintivi">{d.distinctive_traits}</Section>
            )}
            {d.medical_notes && (
              <Section title="Esigenze mediche / patologie" accent="#FF3B30">
                {d.medical_notes}
              </Section>
            )}
            {d.vaccinations && (
              <Section title="Vaccinazioni">{d.vaccinations}</Section>
            )}

            <div className="mt-8 glass rounded-2xl p-5">
              <div className="text-xs uppercase tracking-widest text-[#9A9A9A] mb-2">Contatti proprietario</div>
              <div className="font-display text-xl">{d.owner_name}</div>
              {/* Phone is EXPOSED only when an SOS is active. Otherwise show
                  a friendly locked hint so finders know contact exists. */}
              {em && d.emergency_contact ? (
                <a href={`tel:${d.emergency_contact}`} data-testid="passport-call-owner"
                   className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF3B30] text-white font-medium">
                  <Phone className="h-4 w-4" /> Chiama ora · {d.emergency_contact}
                </a>
              ) : d.has_emergency_contact ? (
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F4EFE6] text-[#7A7A7A] text-[13px]"
                     data-testid="passport-phone-locked">
                  <span>🔒</span> Il telefono si sblocca in caso di SOS attivo
                </div>
              ) : null}
              {em && d.owner_email && (
                <a href={`mailto:${d.owner_email}`} className="mt-3 ml-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F4EFE6] text-[#1A1A1A] font-medium">
                  Scrivi email
                </a>
              )}
            </div>

            {em && (
              <div className="mt-5 glass rounded-2xl p-5 border border-[#FF3B30]/40">
                <div className="text-xs uppercase tracking-widest text-[#FF3B30] mb-1">Ultima posizione segnalata</div>
                <div className="flex items-center gap-2 text-[#1A1A1A]">
                  <MapPin className="h-4 w-4" /> {em.lat.toFixed(4)}, {em.lng.toFixed(4)}
                </div>
                <a
                  href={`https://www.openstreetmap.org/?mlat=${em.lat}&mlon=${em.lng}#map=15/${em.lat}/${em.lng}`}
                  target="_blank" rel="noreferrer"
                  className="mt-3 inline-block text-sm underline text-[#6B6B6B]"
                >
                  Apri su OpenStreetMap →
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Diario — permanent highlights from Stories tagged with this dog. */}
        {highlights && highlights.length > 0 && (
          <section className="mt-12" data-testid="passport-diary">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#9A9A9A] font-medium inline-flex items-center gap-2">
                  <BookOpen className="h-3.5 w-3.5 text-[#FF7A1A]" /> Diario di {d.name}
                </div>
                <h2 className="font-display text-2xl tracking-tight leading-tight mt-1 text-[#1A1A1A]">
                  I momenti che restano
                </h2>
              </div>
              <div className="text-[12px] text-[#7A7A7A] tabular-nums">
                {highlights.length} {highlights.length === 1 ? "momento" : "momenti"}
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3">
              {highlights.map((h) => (
                <DiaryTile key={h.id} h={h} />
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/[0.03] border border-[#F0EBE3] rounded-2xl p-4">
      <div className="flex items-center gap-2 text-[#9A9A9A] text-xs uppercase tracking-wide">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="font-medium mt-1.5">{value}</div>
    </div>
  );
}

function Section({ title, children, accent }) {
  return (
    <div className="mt-6">
      <div className="text-xs uppercase tracking-widest mb-2" style={{ color: accent || "rgba(255,255,255,0.5)" }}>{title}</div>
      <div className="text-[#1A1A1A] leading-relaxed whitespace-pre-line">{children}</div>
    </div>
  );
}

/* ─── Single diary tile — 9:16 thumbnail with play icon on videos ─── */
function DiaryTile({ h }) {
  const isVideo = (h?.media_type || "video") === "video";
  const posterUrl = h?.thumb_url || (!isVideo ? h?.video_url : null);
  const label = h?.caption?.trim();
  return (
    <div
      data-testid={`diary-tile-${h.id}`}
      className="relative rounded-2xl overflow-hidden border border-[#F0EBE3] bg-gradient-to-br from-[#FF7A1A] to-[#F4B83C]"
      style={{ aspectRatio: "9/16", boxShadow: "0 8px 22px -12px rgba(0,0,0,0.22)" }}
    >
      {posterUrl && (
        <img
          src={posterUrl}
          alt={label || "Diario"}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      {/* Bottom gradient for legibility */}
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.65) 100%)" }} />
      {isVideo && (
        <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-white/95 flex items-center justify-center"
             style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.25)" }}>
          <Play className="h-2.5 w-2.5 text-black" fill="black" />
        </div>
      )}
      {label && (
        <div className="absolute inset-x-1.5 bottom-1.5 text-[10px] text-white leading-tight font-medium"
             style={{
               display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
               overflow: "hidden", textShadow: "0 1px 3px rgba(0,0,0,0.7)",
             }}>
          {label}
        </div>
      )}
    </div>
  );
}
